// components/feed/MaterialsFeedSection.js - UPDATED with FullFeedPage navigation support
import React from 'react';
import { useNavigate } from 'react-router-dom';
import { Spinner, Alert } from 'react-bootstrap';
import useCommitteeFeedData from '../../hooks/useCommitteeFeedData';
import FeedItem from './FeedItem';

/**
 * MaterialsFeedSection - Displays committee materials using the committee API
 * Now supports navigation to FullFeedPage with proper committee API parameters
 */
const MaterialsFeedSection = ({ 
  title, 
  apiEndpoint, 
  feedName, 
  branch, 
  topic, 
  buttonName, 
  maxItems = 1,
  showViewMore = false
}) => {
  const navigate = useNavigate();
  const { data, loading, error } = useCommitteeFeedData(apiEndpoint, maxItems);

  /**
   * Parse the committee API endpoint to extract parameters
   * Example endpoint: '&skip=0&committee=Committee%20on%20Agriculture&chamber=HOUSE'
   */
  const parseCommitteeParams = (endpoint) => {
    if (!endpoint) return null;
    
    try {
      // Clean the endpoint
      let cleanEndpoint = endpoint;
      if (cleanEndpoint.startsWith('&') || cleanEndpoint.startsWith('?')) {
        cleanEndpoint = cleanEndpoint.substring(1);
      }
      
      const urlParams = new URLSearchParams(cleanEndpoint);
      return {
        committee: urlParams.get('committee'),
        chamber: urlParams.get('chamber'),
        skip: urlParams.get('skip') || '0'
      };
    } catch (err) {
      console.error('Error parsing committee endpoint:', err);
      return null;
    }
  };

  /**
   * Generate the FullFeedPage URL with committee API parameters
   */
  const generateFullFeedUrl = () => {
    const params = parseCommitteeParams(apiEndpoint);
    
    if (!params || !params.committee) {
      // Fallback to generic committee materials page
      return '/feed/united_states_of_america/committee_materials';
    }
    
    // Build URL with committee API parameters
    const searchParams = new URLSearchParams();
    searchParams.set('useCommitteeAPI', 'true');
    searchParams.set('committee', params.committee);
    if (params.chamber) {
      searchParams.set('chamber', params.chamber);
    }
    
    return `/feed/united_states_of_america/committee_materials?${searchParams.toString()}`;
  };

  /**
   * Handle title click - navigate to full feed
   */
  const handleTitleClick = (e) => {
    e.preventDefault();
    const url = generateFullFeedUrl();
    navigate(url);
  };

  // Loading state
  if (loading) {
    return (
      <div className="feed-section">
        {title && (
          <h4 
            id='sectionHeading' 
            className="section-title"
            onClick={handleTitleClick}
            style={{ cursor: 'pointer' }}
          >
            {title}
          </h4>
        )}
        <div className="d-flex justify-content-center align-items-center" style={{ height: '160px' }}>
          <Spinner id='spinner' animation="grow" variant="light" />
        </div>
      </div>
    );
  }

  // Error state
  if (error) {
    return (
      <div className="feed-section">
        {title && (
          <h4 
            id='sectionHeading' 
            className="section-title"
            onClick={handleTitleClick}
            style={{ cursor: 'pointer' }}
          >
            {title}
          </h4>
        )}
        <Alert variant="warning">
          <p>Unable to load {title ? title.toLowerCase() : 'materials'}:</p>
          <small>{error.message || error}</small>
        </Alert>
      </div>
    );
  }

  // Empty state
  if (!data || data.length === 0) {
    return (
      <div className="feed-section">
        {title && (
          <h4 
            id='sectionHeading' 
            className="section-title"
            onClick={handleTitleClick}
            style={{ cursor: 'pointer' }}
          >
            {title}
          </h4>
        )}
        <Alert variant="info">
          No {title ? title.toLowerCase() : 'materials'} available at this time.
        </Alert>
      </div>
    );
  }

  return (
    <div className="feed-section">
      {title && (
        <h4 
          id='sectionHeading' 
          className="section-title"
          onClick={handleTitleClick}
          style={{ cursor: 'pointer' }}
        >
          {title}
        </h4>
      )}
      
      <div className="feed-items">
        {data.slice(0, maxItems).map((item, index) => (
          <div key={item.id || index} className="mb-3">
            <FeedItem
              item={item}
              feedName={feedName}
              branch={branch}
              topic={topic}
              buttonName={buttonName}
              schema="united_states_of_america"
              table="committee_materials"
              expanded={false}
              variant="homepage"
              showViewMore={showViewMore}
              viewMoreUrl={showViewMore ? generateFullFeedUrl() : null}
            />
          </div>
        ))}
      </div>
    </div>
  );
};

export default MaterialsFeedSection;